import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { wishlistApi } from '@/api'

/**
 * Wishlist Store with API Integration
 * 
 * Supports both local storage (for guests) and server sync (for authenticated users)
 */

const STORAGE_KEY = 'inversun-wishlist'

export const useWishlistStore = defineStore('wishlist', () => {
  // State
  const items = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const isInitialized = ref(false)
  const currentPage = ref(1)
  const lastPage = ref(1)
  const total = ref(0)

  // Load wishlist from localStorage
  const loadWishlist = () => {
    if (typeof window === 'undefined' || isInitialized.value) return
    
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        items.value = JSON.parse(saved)
      }
      isInitialized.value = true
    } catch (e) {
      console.warn('Failed to load wishlist from localStorage:', e)
      items.value = []
    }
  }

  // Save to localStorage
  watch(items, (newItems) => {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems))
    } catch (e) {
      console.warn('Failed to save wishlist to localStorage:', e)
    }
  }, { deep: true })

  // Getters
  const itemCount = computed(() => items.value.length)
  const isEmpty = computed(() => items.value.length === 0)
  
  const isInWishlist = computed(() => (productId) => {
    return items.value.some(item => item.product_id === productId || item.id === productId)
  })

  // Actions
  async function fetchWishlist(page = 1) {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await wishlistApi.getWishlist(page)
      const payload = response?.data || response
      
      if (payload?.items) {
        items.value = payload.items
      }
      
      // Update pagination info
      currentPage.value = payload?.current_page || page
      lastPage.value = payload?.last_page || 1
      total.value = payload?.total || items.value.length
      
      return payload
    } catch (err) {
      error.value = err.message || 'Failed to fetch wishlist'
      console.error('Error fetching wishlist:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function addToWishlist(product) {
    // Check if already in wishlist
    if (isInWishlist.value(product.id)) {
      return
    }

    // Add to local state immediately
    items.value.push({
      id: product.id,
      product_id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      image: product.image,
      category: product.category || null,
    })

    // Sync with server
    try {
      const response = await wishlistApi.addItem(product.id)
      const payload = response?.data || response
      
      // Refresh wishlist to get updated list from server
      if (payload) {
        await fetchWishlist(currentPage.value)
      }
    } catch (err) {
      console.warn('Failed to sync wishlist with server:', err)
    }
  }

  async function removeFromWishlist(productId) {
    const index = items.value.findIndex(item => 
      item.product_id === productId || item.id === productId
    )
    
    if (index > -1) {
      items.value.splice(index, 1)

      // Sync with server
      try {
        await wishlistApi.removeItem(productId)
        // Refresh wishlist to get updated list from server
        await fetchWishlist(currentPage.value)
      } catch (err) {
        console.warn('Failed to remove from server wishlist:', err)
      }
    }
  }

  async function toggleWishlist(product) {
    if (isInWishlist.value(product.id)) {
      await removeFromWishlist(product.id)
    } else {
      await addToWishlist(product)
    }
  }

  function clearWishlist() {
    items.value = []
    currentPage.value = 1
    lastPage.value = 1
    total.value = 0
  }

  return {
    // State
    items,
    isLoading,
    error,
    currentPage,
    lastPage,
    total,
    // Getters
    itemCount,
    isEmpty,
    isInWishlist,
    // Actions
    fetchWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    clearWishlist,
    loadWishlist
  }
})
